// covered-by: frontends/carrie/src/ask-v2/transport/__tests__/applyStreamSnapshot.test.ts
import {
  type AskPunttAgentStep,
  type AskPunttCheckProgress,
  type AskPunttMessage,
} from "@mg/schemas/src/christo/askPuntt";

import { fitCutThisFrame } from "./fitCut";
import {
  isStreamSnapshotMessageId,
  isSynthesizedRow,
  runningTurnUserMessageId,
  runningTurnUserMessageIdFromThread,
  STREAM_MESSAGE_PREFIX,
} from "./runningTurn";
import { type AskV2ThreadMessage } from "./types";

import { type StreamingState } from "../../hooks/useAskPunttStream";

export { STREAM_MESSAGE_PREFIX };

/**
 * The snapshot a *pending* warhol turn publishes to, or null when nothing is
 * pending.
 *
 * Warhol answers whole: while a review runs there is no ANSWER in the history
 * to subscribe on, and the person's own message is what the answer will be
 * filed under. That is the signal — the turn still awaiting an answer is
 * exactly "the answer has not landed", which is also what the history poll runs
 * on. Once the answer arrives it is the tail and this returns null, so the
 * subscription (and its socket) is torn down with the turn.
 *
 * Which message that is — the running turn, the placeholder's marker, and why a
 * follow-up does not move it — is `runningTurnUserMessageId` in
 * `./runningTurn`, shared with the rail so both name one turn.
 */
export function streamMessageIdFor(
  messages: readonly AskPunttMessage[] | undefined,
): string | null {
  const turnUserId = runningTurnUserMessageId(messages);
  return turnUserId ? `${STREAM_MESSAGE_PREFIX}${turnUserId}` : null;
}

/**
 * `streamMessageIdFor`, for a turn read off the LOCAL thread.
 *
 * The address is built here, next to the other one, so the prefix cannot drift
 * between a subscription armed from history and one armed from the transport's
 * own rows. Which turn `runningTurnUserMessageIdFromThread` names — and why a
 * local thread can name it at all — is documented there.
 */
export function streamMessageIdForThread(
  messages: readonly AskV2ThreadMessage[] | undefined,
): string | null {
  const turnUserId = runningTurnUserMessageIdFromThread(messages);
  return turnUserId ? `${STREAM_MESSAGE_PREFIX}${turnUserId}` : null;
}

/**
 * What a step is joined on, or null when it carries nothing either list can
 * match on.
 *
 * `stepId` is the provider's `tool_call_id`, and the one value both producers
 * write: christo names it off the recorded turn
 * (`warholChat.ts::warholAgentSteps`, `stepId: event.toolCallId`) and warhol
 * off its own event stream (`runtime/live_steps.py`), so it is the only field
 * the live list and the mirrored row can be joined on — every other one is a
 * copy, and a copy is what drifts.
 *
 * Keyed by TOOL NAME instead — which is what this did — two calls to one tool
 * in a single turn read as one: the second call and its result were dropped as
 * "already reported" and the chip only appeared when the history poll caught
 * up, which is the poll this overlay exists to stop waiting on.
 *
 * `type` rides in the key because a call and its result carry the SAME
 * `stepId`. Joined on the id alone, a result reads as reported by the call
 * sitting above it and never lands, so its chip spins until the next poll.
 *
 * A step with no id falls back to the tool it names, then to its own content
 * for the two kinds that name none. Nothing warhol publishes is in that case —
 * its reducer always carries the provider's id — so the fallback is what keeps
 * a shape without one from being laid down again on every frame.
 */
function stepKey(step: AskPunttAgentStep): string | null {
  const stepId = "stepId" in step ? step.stepId : undefined;
  if (stepId) return `${step.type}:${stepId}`;
  const tool = stepTool(step);
  if (tool) return `${step.type}:tool:${tool}`;
  return "content" in step ? `${step.type}:${step.content}` : null;
}

/**
 * The live steps the row does not already report, in the live list's own order.
 *
 * `snapshot.agentSteps` is the running turn's thread as warhol builds it from
 * the agent's event stream (`services/warhol/runtime/live_steps.py`): one
 * `tool_call` immediately followed by its `tool_result`, in first-call order.
 * The row's own `agentSteps` are that same turn mirrored by christo from the
 * recorded history, and they LAG it — warhol's `run_subagents` call reaches
 * christo's mirror a poll after the socket already carries its result.
 *
 * So the row is the base and the live list only ADDS. `agentSteps` is a list
 * the history poll grows, and spreading the live list over it is a
 * truncation: from the first settled reviewer to the end of the review the
 * thread drew one "Run subagents" group and lost every row it had accumulated
 * (Search text, Write plan, Run Python — the three the report named), and only
 * a reload brought them back. A step both lists report is left exactly as the
 * row has it, because the row is what the stored message ends up holding and
 * the two agree field for field by construction.
 */
function liveAgentSteps(
  row: AskPunttMessage | undefined,
  steps: readonly unknown[],
): AskPunttAgentStep[] {
  const live = steps.filter(
    (step): step is AskPunttAgentStep =>
      !!step && typeof step === "object" && !Array.isArray(step),
  );
  if (live.length === 0) return [];
  const reported = new Set<string>();
  for (const step of row?.agentSteps ?? []) {
    const key = stepKey(step);
    if (key) reported.add(key);
  }
  const added: AskPunttAgentStep[] = [];
  for (const step of live) {
    const key = stepKey(step);
    if (key && reported.has(key)) continue;
    if (key) reported.add(key);
    added.push(step);
  }
  return added;
}

/**
 * The tool a step names, or undefined for the step kinds that name none.
 *
 * `AskPunttAgentStep` is a discriminated union: `thinking` and `text` carry
 * content, not a tool. A live step with no tool is always laid down — there is
 * nothing in the row for it to contradict.
 */
function stepTool(step: AskPunttAgentStep): string | undefined {
  return "tool" in step ? step.tool : undefined;
}

/**
 * The answer text this overlay last laid down, and the turn it belongs to.
 *
 * The guard above needs what is ON SCREEN, and on a warhol turn the row cannot
 * supply it. A running review takes the FIRST of `useAskV2Transport`'s two
 * branches — the history payload still names the armed address while the answer
 * is unlanded, which is the same signal `streamMessageIdFor` reads above — so
 * the base handed in is the RAW payload (`const base = rawServerMessages ??
 * []`), while christo publishes the running text to the snapshot and only writes
 * `messages.$.streamingContent: null` onto the stored row
 * (`services/christo/lib/services/askPunttChat.ts`). The overlaid list does go
 * back into the transport (`useAskV2Transport` maps `serverMessages` and calls
 * `transport.syncFromServer`), but it re-enters the overlay only as the
 * `transportMessages` of the SECOND branch, taken when the payload cannot name
 * the address at all. On the branch a review actually runs on, reading off the
 * row alone makes `held` null on every warhol frame, so the guard's first
 * conjunct is false, it never refuses anything, and `fit_snapshot`'s halved
 * answer is laid straight down — the flicker the guard exists to stop.
 *
 * One slot, not a map: a turn is named by its snapshot address and only one is
 * live at a time, so a frame for a different address replaces the slot rather
 * than growing it. Losing it costs nothing — a reload re-seeds it from the first
 * frame, which is a whole state document.
 */
let lastAcceptedStreamingContent: {
  messageId: string;
  text: string;
} | null = null;

/** Drops the carry so one test's turn cannot be held by another's text. */
export function __resetStreamingCarryForTests(): void {
  lastAcceptedStreamingContent = null;
}

/** The row's own answer text, or null when it carries none. */
function rowStreamingContent(row: AskPunttMessage | undefined): string | null {
  return typeof row?.streamingContent === "string" && row.streamingContent
    ? row.streamingContent
    : null;
}

/**
 * The text the person is already reading for this turn: the longer of what this
 * overlay last accepted and what the row itself carries.
 *
 * The longer of the two, because either can be the stale one — the carry is a
 * frame ahead of the history poll mid-review, and the row is ahead of the carry
 * in the tab that just reloaded.
 */
function heldStreamingContent(
  messageId: string,
  row: AskPunttMessage | undefined,
): string | null {
  const fromRow = rowStreamingContent(row);
  const carried =
    lastAcceptedStreamingContent?.messageId === messageId
      ? lastAcceptedStreamingContent.text
      : null;
  if (carried === null) return fromRow;
  if (fromRow === null) return carried;
  return fromRow.length > carried.length ? fromRow : carried;
}

/**
 * Whether a live snapshot has anything the thread or live review can draw.
 *
 * A snapshot is published for the whole turn once any check settles, and its
 * `status`/`content` fields say nothing a v2 chat acts on — a frame carrying
 * only those must leave the thread byte-identical, or an empty row appears the
 * moment the first publish lands. A frame that only repeats tool rows the row
 * already has is the same case, and `liveAgentSteps` is what makes it one.
 */
function liveFields(
  snapshot: StreamingState,
  messageId: string,
  row?: AskPunttMessage,
): Partial<AskPunttMessage> | null {
  const added = liveAgentSteps(row, snapshot.agentSteps ?? []);
  const checkProgress = snapshot.checkProgress ?? [];
  // The answer as the model is writing it. `content` is the SETTLED answer and
  // stays empty until christo writes it from the mirror, so while a turn runs
  // this is the only source the words on screen have. Empty means "no text
  // right now" — the reducer resets between parts — and leaves the row's own
  // value alone rather than blanking a paragraph already on screen. A shorter
  // non-empty value is NOT the same case: it may be the size fit giving the
  // answer up last, and `fitCutThisFrame` is what tells the two apart.
  const streamingContent =
    typeof snapshot.streamingContent === "string" && snapshot.streamingContent
      ? snapshot.streamingContent
      : null;
  // What is already on screen — the text a cut frame must not shrink. Why it
  // cannot be read off the row is `heldStreamingContent`.
  const held = heldStreamingContent(messageId, row);
  // The text this frame leaves on screen: its own, or the held text when the
  // size fit cut it. A cut frame RESTATES the held text rather than merely
  // being refused, because the row it would otherwise fall back to carries
  // nothing on a warhol turn — refusing alone takes the paragraph off the
  // screen instead of shrinking it.
  //
  // `held !== null` is the one case the stamp forces this reader to spell out
  // and the shape test never could: the publisher can stamp the FIRST frame a
  // turn ever publishes, and there is nothing on screen to restate. Then the
  // cut text is all the person can be shown, so it is laid down — the next
  // unfitted frame grows it back.
  //
  // The LENGTH test is the other case the stamp alone gets wrong. A cut is only
  // a shrink when it carries LESS than what is on screen, and a cut frame often
  // does not: `fit_snapshot` halves the answer only until the document fits
  // (`services/warhol/runtime/clients/ask_puntt_stream.py`), so as the answer
  // grows its cut grows with it and a later stamped frame carries MORE words
  // than the one this overlay last accepted. Held on the stamp alone the screen
  // freezes at the first accepted cut, and nothing releases it — the publisher
  // re-stamps every write that still carries a cut, so no unstamped frame
  // arrives to end the freeze. A stamped cut is a prefix of the answer, so a
  // longer one can only have added words: lay it down.
  const nextStreamingContent =
    streamingContent === null
      ? null
      : fitCutThisFrame(snapshot, streamingContent, held) &&
          held !== null &&
          streamingContent.length < held.length
        ? held
        : streamingContent;
  if (nextStreamingContent !== null) {
    lastAcceptedStreamingContent = { messageId, text: nextStreamingContent };
  }
  // A value the row already draws is not laid down again: a frame that changes
  // nothing must leave the thread byte-identical.
  const acceptedStreamingContent =
    nextStreamingContent !== null &&
    nextStreamingContent !== rowStreamingContent(row)
      ? nextStreamingContent
      : null;
  if (
    added.length === 0 &&
    checkProgress.length === 0 &&
    acceptedStreamingContent === null
  ) {
    return null;
  }
  return {
    // APPENDED to the row's own, never over them: `agentSteps` is a list the
    // history poll grows, so a spread would be a truncation. See
    // `liveAgentSteps`.
    ...(added.length > 0
      ? { agentSteps: [...(row?.agentSteps ?? []), ...added] }
      : {}),
    ...(checkProgress.length > 0
      ? { checkProgress: checkProgress as AskPunttCheckProgress[] }
      : {}),
    ...(acceptedStreamingContent
      ? { streamingContent: acceptedStreamingContent }
      : {}),
  };
}

/**
 * The index of the row the running turn's live state belongs on, or -1 when the
 * turn has none yet.
 *
 * The turn's own message is the boundary: what follows it, up to the next
 * person's message, is that turn's. Christo's placeholder is the assistant row
 * there (`upsertWarholTurnPlaceholder`), and the agent's whole turn is mirrored
 * onto it, so it is the row every other reader of this thread hangs the live
 * fields off. The overlay's OWN row is there too once one has been synthesised,
 * recognised by its snapshot address.
 *
 * The walk stops at the next user row because a row below it is a LATER turn's
 * — a follow-up the person queued while this one ran has its own placeholder
 * and its own review once its turn starts, and laying this turn's frames on it
 * is how a finished answer starts rewriting itself as the next one streams.
 */
function turnRowIndex(
  messages: readonly AskPunttMessage[],
  turnIndex: number,
  messageId: string,
): number {
  for (let index = turnIndex + 1; index < messages.length; index += 1) {
    const message = messages[index];
    if (!message || message.hidden) continue;
    if (message.role === "user") return -1;
    if (message._id === messageId || !isSynthesizedRow(message)) return index;
  }
  return -1;
}

/**
 * The live snapshot, laid onto the row the running turn belongs to.
 *
 * This is the whole of the v2 stream integration's state. While a review runs
 * the thread is drawn off the socket: the tool rows the history poll has not
 * mirrored yet, the answer text as the model writes it, warhol's own
 * `checkProgress`, and the `subagentOutcomes` of a fan-out the row has not
 * reported yet. The history poll stays the source of truth for the rows both
 * have — the socket only ADDS to the row, never over it — and for the settled
 * answer that ends the turn. Nothing here writes to the server.
 *
 * The row is the turn's OWN, resolved by the same rule the subscription address
 * is built from (`streamMessageIdFor`, which the transport compares against the
 * address its socket is armed on before it hands the list here). That rule is
 * what makes the turn namable at all: christo's `pending` placeholder is the
 * turn's marker and the person's message above it is the turn's, so the address
 * survives a FOLLOW-UP queued behind the running turn — a message whose own
 * turn warhol has not started. Reading the tail instead put christo's
 * placeholder and the overlay's row on two different messages the moment a
 * follow-up was served below it: the tail was the follow-up, a user row, so the
 * overlay found no row to merge into, synthesised a SECOND one for the running
 * turn, and appended it under the follow-up. The person saw the live answer
 * twice, the second copy below a message that had not been answered.
 *
 * The turn's row is christo's placeholder when it has one, and the overlay's own
 * row when it does not. With neither, a row is synthesised directly under the
 * turn's message — warhol writes no placeholder of its own while it reviews, so
 * that case is the normal first seconds of a turn and not a fallback. Returns
 * the SAME array when there is nothing live to show, so a `useMemo` over it does
 * not churn the thread on every idle frame.
 */
export function applyStreamSnapshot(
  messages: AskPunttMessage[],
  snapshot: StreamingState | null | undefined,
): AskPunttMessage[] {
  if (!snapshot) return messages;

  const messageId = streamMessageIdFor(messages);
  // why-not: no `_id` fallback invented here. Without a stream id there is no
  // snapshot either, so the row would only ever be keyed by something no reader
  // can match; `streamMessageIdFor` and this share one definition.
  if (!isStreamSnapshotMessageId(messageId)) return messages;

  const turnUserId = messageId.slice(STREAM_MESSAGE_PREFIX.length);
  const turnIndex = messages.findIndex(
    (message) => message?._id === turnUserId,
  );
  if (turnIndex < 0) return messages;

  const rowIndex = turnRowIndex(messages, turnIndex, messageId);
  const row = rowIndex < 0 ? undefined : messages[rowIndex];
  // The row is the merge's other side: `liveAgentSteps` reads what it already
  // reports, so which fields are live is a property of the row, not the frame.
  const live = liveFields(snapshot, messageId, row);
  if (!live) return messages;

  if (rowIndex >= 0) {
    return messages.map((message, index) =>
      index === rowIndex ? { ...message, ...live } : message,
    );
  }

  const turn = messages[turnIndex];
  return [
    ...messages.slice(0, turnIndex + 1),
    {
      _id: messageId,
      chatId: turn.chatId,
      role: "assistant" as const,
      content: "",
      status: "pending" as const,
      createdAt: turn.createdAt,
      ...live,
    },
    ...messages.slice(turnIndex + 1),
  ];
}
